import { i18n } from '../../../../i18n/i18n';
import { fetchWithAuth } from '../../../helpers/utils';

export interface TincanSessionData {
  scormToken: string;
  tincanDataId: number;
  componentId: number;
}

export interface TincanHealthSettings {
  heartbeatIntervalMs: number;
  healthCheckUrl: string;
  analyticsUrl: string;
}

export interface TincanHealthCallbacks {
  forceCloseContentWindow: () => void;
  luPopoverNotify?: (title: string, message: string) => Promise<void>;
  tinCanClosed?: () => void;
}

export interface TincanHealthServiceConfig {
  session: TincanSessionData;
  settings: TincanHealthSettings;
  callbacks: TincanHealthCallbacks;
}

/**
 * A service to monitor TinCan API health and handle progression errors.
 */
export class TincanHealthService {
  private static instance: TincanHealthService;

  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _progressionErrorOccurred = false;
  private _config: TincanHealthServiceConfig | null = null;
  private _isInitialized = false;

  // Private constructor to enforce singleton pattern.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance(): TincanHealthService {
    if (!TincanHealthService.instance) {
      TincanHealthService.instance = new TincanHealthService();
    }
    return TincanHealthService.instance;
  }

  private get _isDevMode(): boolean {
    return import.meta.env.MODE !== 'production';
  }

  private _log(...args: unknown[]): void {
    if (this._isDevMode) {
      // eslint-disable-next-line no-console
      console.log('[TincanHealthService]', ...args);
    }
  }

  /**
   * Initializes the Tin Can health service.
   * @param config - The configuration object for the health service.
   * @returns
   */
  public init(config: TincanHealthServiceConfig): void {
    if (this._isInitialized) {
      this._log('WARN: TincanHealthService is already initialized. Call teardown() first to re-initialize.');
      return;
    }
    if (!config.session || !config.settings || !config.callbacks) {
      throw new Error('TincanHealthService config is missing required groups: session, settings, callbacks');
    }
    this._config = config;
    this._isInitialized = true;
    this._progressionErrorOccurred = false;
  }

  /**
   * Starts the Tin Can progression health monitoring.
   * @returns
   */
  public start(): void {
    if (!this._isInitialized) {
      this._log('ERROR: TincanHealthService is not initialized. Call init() before starting.');
      return;
    }

    this.teardown();

    const interval = this._config?.settings.heartbeatIntervalMs;
    if (interval) {
      this._log(`Starting Tin Can heartbeat timer (${interval / 1000}s interval).`);
      this._heartbeatTimer = setInterval(this.healthCheck, interval);
    }
  }

  /**
   * Stops the Tin Can health monitoring.
   * @returns
   */
  public teardown(): void {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
      this._log('Cleared Tin Can heartbeat timer.');
    }
  }

  /**
   * Performs a health check on the Tin Can API.
   * @returns A promise that resolves when the health check is complete.
   */
  private healthCheck = async (): Promise<void> => {
    if (this._progressionErrorOccurred) {
      this._log('Progression error already occurred. Stopping heartbeat.');
      this.teardown();
      return;
    }

    const config = this._config;
    if (!config) return;
    const { session, settings } = config;
    const url = `${settings.healthCheckUrl}?slt=${session.scormToken}&tincandata_id=${session.tincanDataId}&component_id=${session.componentId}&skip_session_renew=true`;

    try {
      const response = await fetchWithAuth(url, { method: 'HEAD', cache: 'no-store' });

      if (response.ok) {
        this._log('Tin Can API ping successful.');
        return;
      }

      // The error handling is deliberately specific. The failure workflow is disruptive
      // to the user experience (it closes the content window), so it is only triggered
      // for HTTP status codes 401 and 500. Based on log analysis, these are the only
      // statuses requiring this action. e.g. 404 is valid. If we discover we need to
      // handle for other statuses, we will add them in future iterations.
      if (response.status === 401 || response.status === 500) {
        this._log(`WARN: Critical error detected (status ${response.status}). Handling progression error.`);
        this.handleProgressionError(response.status);
      }
    } catch (error) {
      this._log('ERROR: Network error during Tin Can API health check:', error);
    }
  };

  /**
   * Handles progression errors by stopping the heartbeat and showing an error dialog.
   * @param httpStatus - The HTTP status code indicating the type of error.
   */
  private handleProgressionError(httpStatus: number): void {
    this._progressionErrorOccurred = true;
    this.teardown();
    this.showProgressionErrorDialog(httpStatus);
    this.sendAnalytics();
  }

  /**
   * Shows a dialog to inform the user about a progression error.
   * @param httpStatus - The HTTP status code indicating the type of error.
   */
  private showProgressionErrorDialog(httpStatus: number): void {
    const config = this._config;
    if (!config) return;
    const { callbacks } = config;
    callbacks.forceCloseContentWindow();

    const title = String(i18n('lua.progression.tincan.health.error_title'));
    const message =
      httpStatus === 401
        ? String(i18n('lua.progression.tincan.health.error_unauthorized'))
        : String(i18n('lua.progression.tincan.health.error_server'));

    const onDialogClose = () => {
      if (typeof callbacks.tinCanClosed === 'function') {
        callbacks.tinCanClosed();
      }
    };

    if (typeof callbacks.luPopoverNotify === 'function') {
      callbacks.luPopoverNotify(title, message).then(onDialogClose);
    } else {
      alert(message);
      onDialogClose();
    }
  }

  /**
   * Sends an analytics beacon to track progression interruptions.
   */
  private sendAnalytics(): void {
    const csrfParam = (document.querySelector('meta[name="csrf-param"]') as HTMLMetaElement)?.content;
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content;

    if (!csrfParam || !csrfToken) {
      this._log('ERROR: CSRF meta tags not found for analytics.');
      return;
    }

    const config = this._config;
    if (!config) return;
    const { session, settings } = config;
    const timestamp = Date.now().toString();
    const url = `${settings.analyticsUrl}?slt=${session.scormToken}&skip_session_renew=true&_=${timestamp}`;

    const payload = new FormData();
    payload.append('local_timestamp', timestamp);
    payload.append(csrfParam, csrfToken);

    const authToken = localStorage.getItem('authToken');

    // navigator.sendBeacon does not support custom headers, so we must use fetch if an authToken is present.
    if (navigator.sendBeacon && !authToken) {
      navigator.sendBeacon(url, payload);
    } else {
      fetchWithAuth(url, {
        method: 'POST',
        body: payload,
        keepalive: true,
      });
    }
  }
}
